import { useEffect, useRef, useState, type ComponentType } from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Briefcase,
  Building2,
  CalendarDays,
  ChartColumn,
  ChevronDown,
  Copy,
  CreditCard,
  FileText,
  History,
  LayoutDashboard,
  ListChecks,
  Mail,
  Megaphone,
  MessageSquare,
  MoreHorizontal,
  Pin,
  Receipt,
  Repeat,
  ScrollText,
  Settings,
  Settings2,
  Ticket,
  UserPlus,
  Video,
  X,
} from "lucide-react";
import { Link } from "react-router";

import { IconButton } from "@/components/ui/icon-button";
import { InlineIconButton, Pressable } from "@/components/ui/pressable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useWorkspaceTabs } from "@/modules/workspace/workspaceTabsContext";
import {
  workspaceTabKind,
  type WorkspaceTab,
  type WorkspaceTabKind,
} from "@/modules/workspace/workspaceTabs";
import {
  useWorkspaceTabSignals,
  type WorkspaceTabSignal,
} from "@/modules/workspace/useWorkspaceTabSignals";
import { cn } from "@/lib/utils";
import { toneClass } from "@/modules/shared/status/tone";

const PREVIEW_HINT = "Preview: the next record opens here.";

const KIND_ICON: Record<
  WorkspaceTabKind,
  ComponentType<{ className?: string }>
> = {
  ticket: Ticket,
  invoice: Receipt,
  subscription: Repeat,
  proposal: ScrollText,
  billing: CreditCard,
  meeting: Video,
  calendar: CalendarDays,
  account: Building2,
  project: Briefcase,
  lead: UserPlus,
  task: ListChecks,
  mail: Mail,
  message: MessageSquare,
  marketing: Megaphone,
  report: ChartColumn,
  settings: Settings,
  dashboard: LayoutDashboard,
  page: FileText,
};

const TabIcon = ({
  tab,
  className,
}: {
  tab: WorkspaceTab;
  className?: string;
}) => {
  const Icon = KIND_ICON[workspaceTabKind(tab)];
  return <Icon className={cn("size-3.5 shrink-0", className)} />;
};

/** Actions on one tab, shared by its right-click menu and the bar menu. */
const TabActions = ({ tab }: { tab: WorkspaceTab }) => {
  const {
    tabs,
    pinTab,
    duplicateTab,
    closeTab,
    closeOtherTabs,
    closeTabsToRight,
  } = useWorkspaceTabs();
  const index = tabs.findIndex((item) => item.id === tab.id);
  return (
    <>
      {!tab.pinned ? (
        <DropdownMenuItem onSelect={() => pinTab(tab.id)}>
          <Pin />
          Keep open
        </DropdownMenuItem>
      ) : null}
      <DropdownMenuItem onSelect={() => duplicateTab(tab.id)}>
        <Copy />
        Duplicate
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={() => closeTab(tab.id)}>
        <X />
        Close
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={tabs.length < 2}
        onSelect={() => closeOtherTabs(tab.id)}
      >
        Close other tabs
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={index < 0 || index >= tabs.length - 1}
        onSelect={() => closeTabsToRight(tab.id)}
      >
        Close tabs to the right
      </DropdownMenuItem>
    </>
  );
};

/** Every open tab, for the bar menu and the phone list. */
const AllTabsList = ({
  signals,
}: {
  signals: Map<string, WorkspaceTabSignal>;
}) => {
  const { tabs, activeId, dirtyIds, selectTab } = useWorkspaceTabs();
  return (
    <>
      <DropdownMenuLabel>Open tabs</DropdownMenuLabel>
      {tabs.map((tab) => (
        <DropdownMenuItem
          key={tab.id}
          onSelect={() => selectTab(tab.id)}
          className={cn(tab.id === activeId && "bg-accent")}
        >
          <TabIcon tab={tab} className="text-muted-foreground" />
          <span
            className={cn("min-w-0 flex-1 truncate", !tab.pinned && "italic")}
          >
            {tab.title}
          </span>
          <TabBadge signal={signals.get(tab.id)} dirty={dirtyIds.has(tab.id)} />
        </DropdownMenuItem>
      ))}
    </>
  );
};

const BarMenuFooter = () => {
  const { canReopen, reopenClosedTab } = useWorkspaceTabs();
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        disabled={!canReopen}
        onSelect={() => reopenClosedTab()}
      >
        <History />
        Reopen closed tab
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link to="/profile">
          <Settings2 />
          Tab settings
        </Link>
      </DropdownMenuItem>
    </>
  );
};

/** One dot: news first (reply, paid), then an unsent draft. */
const TabBadge = ({
  signal,
  dirty,
}: {
  signal?: WorkspaceTabSignal;
  dirty: boolean;
}) => {
  if (signal?.badge === "reply") {
    return (
      <span
        className="relative flex size-2 shrink-0"
        title="New reply from the client"
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-primary/60" />
        <span className="relative size-2 rounded-full bg-primary" />
      </span>
    );
  }
  if (signal?.badge === "paid") {
    return (
      <span
        className={cn(
          "shrink-0 rounded-sm px-1 text-3xs font-semibold uppercase",
          toneClass("success", "solid"),
        )}
        title="Paid since you left this tab"
      >
        Paid
      </span>
    );
  }
  if (dirty) {
    return (
      <span
        className="size-1.5 shrink-0 rounded-full bg-muted-foreground"
        title="Unsent draft"
      />
    );
  }
  return null;
};

const WorkspaceTabChip = ({
  tab,
  selected,
  signal,
}: {
  tab: WorkspaceTab;
  selected: boolean;
  signal?: WorkspaceTabSignal;
}) => {
  const { dirtyIds, selectTab, closeTab, pinTab } = useWorkspaceTabs();
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const dirty = dirtyIds.has(tab.id);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  useEffect(() => {
    if (selected) {
      ref.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [selected]);

  return (
    <div
      ref={(node) => {
        ref.current = node;
        setNodeRef(node);
      }}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...attributes}
      {...listeners}
      // The tab button inside is the focus target; the wrapper only drags.
      tabIndex={-1}
      role="presentation"
      onDoubleClick={() => pinTab(tab.id)}
      onAuxClick={(event) => {
        if (event.button !== 1) return;
        event.preventDefault();
        closeTab(tab.id);
      }}
      onContextMenu={(event) => {
        event.preventDefault();
        setMenuOpen(true);
      }}
      className={cn(
        "group relative flex h-8 min-w-[6.5rem] max-w-[13rem] flex-[0_1_13rem] items-center gap-1.5 rounded-lg border pr-1 pl-2",
        "animate-in fade-in-0 slide-in-from-left-1 duration-150",
        selected
          ? "border-border bg-background text-foreground shadow-sm"
          : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground",
        isDragging && "z-10 opacity-80 shadow-raised",
      )}
    >
      <TabIcon tab={tab} className={selected ? "text-primary" : undefined} />
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <Pressable
            role="tab"
            aria-selected={selected}
            className={cn(
              "min-w-0 flex-1 truncate text-left text-xs font-medium",
              !tab.pinned && "italic",
            )}
            onClick={() => selectTab(tab.id)}
          >
            {tab.title}
          </Pressable>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-72">
          <p className="font-medium">{tab.title}</p>
          {signal?.details.map((line) => (
            <p key={line} className="opacity-80">
              {line}
            </p>
          ))}
          {signal?.badge === "reply" ? (
            <p className="opacity-80">New reply since you left</p>
          ) : null}
          {dirty ? <p className="opacity-80">Unsent draft</p> : null}
          {!tab.pinned ? <p className="opacity-60">{PREVIEW_HINT}</p> : null}
        </TooltipContent>
      </Tooltip>
      <TabBadge signal={signal} dirty={dirty} />
      {!tab.pinned ? (
        <InlineIconButton
          size="xs"
          aria-label={`Keep ${tab.title} open`}
          title="Keep open — the next record opens in a new tab"
          className="hidden group-hover:inline-flex focus-visible:inline-flex"
          onClick={() => pinTab(tab.id)}
        >
          <Pin />
        </InlineIconButton>
      ) : null}
      <InlineIconButton
        size="xs"
        aria-label={`Close ${tab.title}`}
        className={cn(
          !selected &&
            "opacity-0 group-hover:opacity-100 focus-visible:opacity-100",
        )}
        onClick={() => closeTab(tab.id)}
      >
        <X />
      </InlineIconButton>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {/* Anchor only: the menu opens on right-click, not on press. */}
        <DropdownMenuTrigger asChild>
          <span
            aria-hidden
            className="pointer-events-none absolute bottom-0 left-2 size-0"
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          <TabActions tab={tab} />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

/** Phones: the open page plus a list of the others. */
const CompactWorkspaceTabs = ({
  signals,
}: {
  signals: Map<string, WorkspaceTabSignal>;
}) => {
  const { tabs, activeId, closeTab } = useWorkspaceTabs();
  const selected = tabs.find((tab) => tab.id === activeId) ?? tabs[0];
  if (!selected) return null;
  const news = tabs.some((tab) => signals.get(tab.id)?.badge);
  return (
    <div className="flex shrink-0 items-center gap-2 border-b bg-card px-3 py-1.5 print:hidden">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Pressable className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-lg bg-muted/60 px-2.5 text-left text-xs font-medium">
            <TabIcon tab={selected} className="text-primary" />
            <span
              className={cn(
                "min-w-0 flex-1 truncate",
                !selected.pinned && "italic",
              )}
            >
              {selected.title}
            </span>
            <span className="relative shrink-0 rounded-md bg-background px-1.5 py-0.5 text-2xs tabular-nums text-muted-foreground">
              {tabs.length} tabs
              {news ? (
                <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-primary" />
              ) : null}
            </span>
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          </Pressable>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[min(20rem,calc(100vw-2rem))]"
        >
          <AllTabsList signals={signals} />
          <DropdownMenuSeparator />
          <TabActions tab={selected} />
          <BarMenuFooter />
        </DropdownMenuContent>
      </DropdownMenu>
      <IconButton
        aria-label={`Close ${selected.title}`}
        className="size-8"
        onClick={() => closeTab(selected.id)}
      >
        <X />
      </IconButton>
    </div>
  );
};

export const WorkspaceTabBar = ({
  placement = "strip",
  compact = false,
  className,
}: {
  /**
   * `inline` sits in the top bar's own row, between the page actions and the
   * global buttons. `header` is a row of its own in the top bar. `strip` is
   * the fallback when that bar is hidden.
   */
  placement?: "inline" | "header" | "strip";
  /** One row with a tab list, for phones. */
  compact?: boolean;
  className?: string;
}) => {
  const { tabs, activeId, enabled, moveTab } = useWorkspaceTabs();
  const signals = useWorkspaceTabSignals(tabs, activeId);
  const selected = tabs.find((tab) => tab.id === activeId);
  const sensors = useSensors(
    // A small move starts a drag; a click still selects the tab.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  if (!enabled || tabs.length < 2) return null;
  if (compact) return <CompactWorkspaceTabs signals={signals} />;

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id)
      moveTab(String(active.id), String(over.id));
  };

  return (
    <div
      className={cn(
        "flex min-w-0 items-center gap-1 print:hidden",
        placement === "inline"
          ? "flex-1 border-l pl-2"
          : placement === "header"
            ? "shrink-0 border-b px-3 py-1.5"
            : "shrink-0 border-b bg-card px-3 py-1.5",
        className,
      )}
    >
      <div
        role="tablist"
        aria-label="Open work"
        className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={tabs.map((tab) => tab.id)}
            strategy={horizontalListSortingStrategy}
          >
            {tabs.map((tab) => (
              <WorkspaceTabChip
                key={tab.id}
                tab={tab}
                selected={tab.id === activeId}
                signal={signals.get(tab.id)}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton aria-label="All tabs and options" className="size-7">
            <MoreHorizontal />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          <AllTabsList signals={signals} />
          {selected ? (
            <>
              <DropdownMenuSeparator />
              <TabActions tab={selected} />
            </>
          ) : null}
          <BarMenuFooter />
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
